import axios from 'axios';
import api from '@/lib/axios';
import { useAuthStore } from '@/stores/auth-store';

// API local que corre dentro de Electron (puerto 4000). La Nube se alcanza con
// el header 'x-bypass-local' (ver interceptor en lib/axios.ts).
const LOCAL_API_BASE = 'http://127.0.0.1:4000/local-api';

// Evita que el botón manual y el auto-sync corran al mismo tiempo.
let syncInProgress = false;

export interface CloudSyncOptions {
  onStatus?: (message: string) => void;
}

export interface CloudSyncResult {
  ok: boolean;
  skipped?: boolean;
  message?: string;
}

/**
 * Motor de sincronización híbrida (Local <-> Nube) — sync completo.
 *
 * Orden:
 *  1. Push catálogos base (categorías, marcas, unidades, impuestos)
 *  2. Push productos (+ taxes por producto)
 *  3. Push operaciones pendientes (ventas, caja, compras, traspasos, etc.)
 *  4. Marcar synced solo lo enviado
 *  5. Pull catálogo maestro (upsert, no wipe ciego) + roles
 *  6. Pull usuarios offline
 */
export async function runCloudSync(options: CloudSyncOptions = {}): Promise<CloudSyncResult> {
  if (syncInProgress) {
    return { ok: true, skipped: true, message: 'Sincronización ya en curso' };
  }
  syncInProgress = true;

  const { onStatus } = options;
  const status = (msg: string) => {
    try {
      onStatus?.(msg);
    } catch {
      // el callback nunca debe romper el sync
    }
  };

  const localApi = axios.create({ baseURL: LOCAL_API_BASE, timeout: 180000 });
  const cloud = { headers: { 'x-bypass-local': 'true' } } as const;

  try {
    const user = useAuthStore.getState().user;
    const companyId = user?.company?.id || localStorage.getItem('COMPANY_ID');
    if (!companyId) {
      return { ok: true, message: 'Sin empresa configurada' };
    }

    // ── 1. PUSH: catálogos base (antes que productos) ──
    status('Subiendo categorías, marcas, unidades e impuestos...');
    const [categoriesRes, brandsRes, unitsRes, taxesRes] = await Promise.all([
      localApi.get('/categories', { params: { companyId } }),
      localApi.get('/brands', { params: { companyId } }),
      localApi.get('/units', { params: { companyId } }),
      // Incluir inactivos para propagar borrados (soft-delete) a la nube
      localApi.get(`/taxes/company/${companyId}`, { params: { all: '1' } }),
    ]);
    const categories: any[] = Array.isArray(categoriesRes.data) ? categoriesRes.data : categoriesRes.data?.data || [];
    const brands: any[] = Array.isArray(brandsRes.data) ? brandsRes.data : brandsRes.data?.data || [];
    const units: any[] = Array.isArray(unitsRes.data) ? unitsRes.data : unitsRes.data?.data || [];
    const taxes: any[] = Array.isArray(taxesRes.data) ? taxesRes.data : taxesRes.data?.data || [];

    await api.post(
      '/sync/push',
      { categories, brands, units, taxes },
      cloud,
    );

    // ── 2. PUSH: catálogo de productos ──
    status('Leyendo catálogo local de productos...');
    const localProductsRes = await localApi.get('/products', {
      params: { companyId, limit: 10000 },
    });
    const localProducts: any[] = localProductsRes.data?.data || [];
    if (localProducts.length > 0) {
      status(`Subiendo ${localProducts.length} productos a la Nube...`);
      await api.post('/products/sync', { products: localProducts }, cloud);
    }

    // ── 3. PUSH: operaciones offline pendientes ──
    status('Buscando ventas y operaciones offline...');
    const offlineRes = await localApi.get('/sync/export-offline-data');
    const offline = offlineRes.data || {};
    const sales: any[] = offline.sales || [];
    const cashRegisters: any[] = offline.cashRegisters || [];
    const cashMovements: any[] = offline.cashMovements || [];
    const productUpdates: any[] = offline.productUpdates || [];
    const inventoryAdjustments: any[] = offline.inventoryAdjustments || [];
    const purchaseOrders: any[] = offline.purchaseOrders || [];
    const customerPayments: any[] = offline.customerPayments || [];
    const creditNotes: any[] = offline.creditNotes || [];
    const expenses: any[] = offline.expenses || [];
    const supplierPayments: any[] = offline.supplierPayments || [];
    const transfers: any[] = offline.transfers || [];

    status('Leyendo clientes, proveedores y categorías de gasto...');
    const [customersRes, suppliersRes, expenseCatsRes] = await Promise.all([
      localApi.get('/customers', { params: { companyId } }),
      localApi.get('/suppliers', { params: { companyId } }),
      localApi.get('/expense-categories', { params: { companyId } }),
    ]);
    const customers: any[] = Array.isArray(customersRes.data) ? customersRes.data : customersRes.data?.data || [];
    const suppliers: any[] = Array.isArray(suppliersRes.data) ? suppliersRes.data : suppliersRes.data?.data || [];
    const expenseCategories: any[] = Array.isArray(expenseCatsRes.data)
      ? expenseCatsRes.data
      : expenseCatsRes.data?.data || [];

    const hasPendingOperations = [
      sales,
      cashRegisters,
      cashMovements,
      purchaseOrders,
      transfers,
      customerPayments,
      creditNotes,
      expenses,
      supplierPayments,
    ].some((list) => list.length > 0);

    if (
      hasPendingOperations ||
      customers.length > 0 ||
      suppliers.length > 0 ||
      expenseCategories.length > 0
    ) {
      status(
        `Subiendo ${sales.length} ventas, ${transfers.length} traspasos y operaciones offline...`,
      );
      await api.post(
        '/sync/push',
        {
          sales,
          cashRegisters,
          cashMovements,
          purchaseOrders,
          customers,
          suppliers,
          expenseCategories,
          customerPayments,
          creditNotes,
          expenses,
          supplierPayments,
          transfers,
        },
        cloud,
      );
    }

    // Ajustes: idempotentes en la API (no reaplican stock si ya existen)
    if (inventoryAdjustments.length > 0) {
      status(`Registrando ${inventoryAdjustments.length} ajustes de inventario...`);
      await api.post('/inventory/sync-adjustments', { adjustments: inventoryAdjustments }, cloud);
    }

    // ── 4. Marcar como sincronizado en la BD local ──
    const anyPending =
      hasPendingOperations ||
      productUpdates.length > 0 ||
      inventoryAdjustments.length > 0;
    if (anyPending) {
      await localApi.post('/sync/mark-all-synced', {
        saleIds: sales.map((s: any) => s.id),
        cashRegisterIds: cashRegisters.map((c: any) => c.id),
        cashMovementIds: cashMovements.map((m: any) => m.id),
        productUpdateIds: productUpdates.map((p: any) => p.id),
        inventoryAdjustmentIds: inventoryAdjustments.map((a: any) => a.id),
        purchaseOrderIds: purchaseOrders.map((p: any) => p.id),
        customerPaymentIds: customerPayments.map((cp: any) => cp.id),
        creditNoteIds: creditNotes.map((cn: any) => cn.id),
        expenseIds: expenses.map((e: any) => e.id),
        supplierPaymentIds: supplierPayments.map((sp: any) => sp.id),
        transferIds: transfers.map((t: any) => t.id),
      });
    }

    // ── 5. PULL: catálogo maestro Nube -> Local (upsert) ──
    status('Descargando catálogo de la Nube...');
    const pullRes = await api.get('/sync/pull', cloud);
    const catalog = pullRes.data?.data || {};

    let companyInfo: any = null;
    try {
      companyInfo = (await api.get(`/companies/${companyId}`, cloud)).data;
    } catch {
      // Si falla, el local crea/actualiza con lo que tenga.
    }

    // Preferir la empresa del catálogo de pull (misma fuente)
    const companyFromPull = catalog.company || null;
    await localApi.post('/sync/pull', {
      companyId,
      company: companyFromPull || companyInfo,
      products: catalog.products || [],
      categories: catalog.categories || [],
      brands: catalog.brands || [],
      units: catalog.units || [],
      customers: catalog.customers || [],
      suppliers: catalog.suppliers || [],
      expenseCategories: catalog.expenseCategories || [],
      taxes: catalog.taxes || [],
      promotions: catalog.promotions || [],
      roles: catalog.roles || [],
      upsert: true,
    });

    // ── 6. PULL: usuarios para login offline ──
    try {
      status('Actualizando usuarios para modo offline...');
      const usersRes = await api.get('/users/sync', { params: { companyId }, ...cloud });
      const users = Array.isArray(usersRes.data) ? usersRes.data : usersRes.data?.data || [];
      if (Array.isArray(users) && users.length > 0) {
        await localApi.post('/auth/sync', { users });
      }
    } catch (err) {
      console.warn('[CloudSync] No se pudieron actualizar los usuarios locales:', err);
    }

    status('Sincronización completada exitosamente.');
    return { ok: true, message: 'Sincronización completada exitosamente.' };
  } catch (err: any) {
    const message =
      err?.response?.data?.message || err?.message || 'Ocurrió un error al sincronizar';
    console.error('[CloudSync] Error:', err);
    return { ok: false, message };
  } finally {
    syncInProgress = false;
  }
}
